import i18n from 'i18next';

import { initReactI18next } from "react-i18next";
import { i18nextPlugin } from 'translation-check';
import Backend from 'i18next-http-backend';

/**
 * Discovers and bundles all locale JSON files at build time using webpack's
 * `require.context`. Files from every language subdirectory are merged into
 * a single `resources` object keyed by locale code.
 *
 * This approach is only used in development because `require.context` is a
 * webpack-specific API that doesn't exist at runtime in a served build.
 * In production, `i18next-http-backend` fetches locale files over HTTP instead,
 * avoiding the overhead of bundling all translations into the JS bundle.
 *
 * @returns {Record<string, { translation: Record<string, *> }>}
 */
const loadTranslations = () => {
  const resources = {};

  const context = require.context('../public/locales', true, /\.json$/);

  context.keys().forEach((path) => {
    // Extract locale from path (e.g., './en/translation.json' -> 'en')
    const locale = path.split('/')[1];

    if (!resources[locale]) {
      resources[locale] = {
        translation: {}
      };
    }

    // Merge all JSON files within a locale directory into one translation object.
    // This allows splitting translations across multiple files per locale if needed
    // in future, without changing the consumer API.
    Object.assign(
      resources[locale].translation,
      context(path)
    );
  });
  return resources;
};

let i18nConfig = {
  fallbackLng: 'en',
  debug: false,
  interpolation: {
    // React already escapes output; double-escaping would corrupt HTML entities in translations.
    escapeValue: false,
  },
  ns: ['translation'],
  defaultNS: 'translation',
};

if (process.env.NODE_ENV === 'development') {
  console.log('development');
  // translation-check plugin logs warnings for keys present in one locale but
  // missing in another, making it easy to catch incomplete translations early.
  i18n.use(i18nextPlugin);
  i18nConfig = {
    ...i18nConfig,
    resources: loadTranslations(),
  }
} else {
  console.log('production');
  i18n.use(Backend);
  i18nConfig = {
    ...i18nConfig,
    backend: {
      // Resolves to e.g. /locales/en/translation.json — served as a static file
      // from the build output, so the browser fetches only the active language.
      loadPath: (lng, ns) => {
        return `/locales/${lng}/${ns}.json`;
      },
    },
  }
}

i18n
  .use(initReactI18next)
  .init(i18nConfig);

export default i18n;
